import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MessageSquare, Save, RefreshCw, Loader2, Check, VolumeX, Volume2, BellOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHANNELS = [
  { key: 'to_self',     label: 'Staff (self)',  desc: 'The staff who triggered the action' },
  { key: 'to_admin',    label: 'All Admins',    desc: 'Every user with the Admin role' },
  { key: 'to_report_1', label: 'Report 1',      desc: 'Direct manager (reports_to_1)' },
  { key: 'to_report_2', label: 'Report 2',      desc: 'Skip-level (reports_to_2)' },
  { key: 'to_report_3', label: 'Report 3',      desc: 'Top-level (reports_to_3)' },
];

/**
 * WhatsappSettings — admin matrix to control which template goes to whom.
 *
 * Rows = template names (visit_start, lunch_start, …)
 * Cols = recipient channels (self / admin / report 1-3)
 *
 * Backend: `/api/admin/whatsapp-settings` (GET/PUT). Defaults are seeded
 * server-side so a fresh install never goes silent.
 */
export default function WhatsappSettings() {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('');

  const fetchSettings = async () => {
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTemplates(d.templates || {});
    } catch (e) {
      setErr(`Failed to load settings: ${e.message || e}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  // Counts of templates currently fully-silenced (all 5 channels off).
  // Shown as a chip at the top of the page so admins can see at a glance
  // how many message types they've muted — directly addressing the user
  // complaint that "messages were going out that I never approved".
  const silencedCount = useMemo(
    () =>
      Object.values(templates).filter(
        (row) => CHANNELS.every((c) => !row[c.key])
      ).length,
    [templates]
  );

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return Object.entries(templates)
      .filter(([k, v]) => !q || k.includes(q) || (v.label || '').toLowerCase().includes(q))
      .sort((a, b) => {
        // Sort silenced rows to the TOP so admins notice what's currently muted.
        const aOff = CHANNELS.every((c) => !a[1][c.key]);
        const bOff = CHANNELS.every((c) => !b[1][c.key]);
        if (aOff !== bOff) return aOff ? -1 : 1;
        return a[0].localeCompare(b[0]);
      });
  }, [templates, filter]);

  const toggle = (name, channel) => {
    setTemplates(prev => ({
      ...prev,
      [name]: { ...prev[name], [channel]: !prev[name][channel] }
    }));
  };

  const setAllForRow = (name, value) => {
    setTemplates(prev => ({
      ...prev,
      [name]: { ...prev[name], ...Object.fromEntries(CHANNELS.map(c => [c.key, value])) }
    }));
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp-settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ templates }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTemplates(d.templates || {});
      setSavedAt(new Date());
    } catch (e) {
      setErr(`Failed to save: ${e.message || e}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-settings-page">
      <div className="flex flex-wrap items-center gap-3">
        <MessageSquare className="text-emerald-600" size={22} />
        <h1 className="text-xl font-bold text-gray-900">WhatsApp Settings</h1>
        <span className="text-xs text-gray-500">Control which template goes to whom — uncheck to silence.</span>
        {silencedCount > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-700 border border-red-200"
            data-testid="whatsapp-silenced-summary"
            title="Templates with every channel turned off"
          >
            <VolumeX size={12} /> {silencedCount} silenced
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={fetchSettings}
          disabled={loading || saving}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 hover:bg-gray-50 rounded-lg"
          data-testid="whatsapp-settings-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Reload
        </button>
        <button
          onClick={save}
          disabled={loading || saving}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-lg"
          data-testid="whatsapp-settings-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="whatsapp-settings-err">{err}</div>
      )}
      {savedAt && !err && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-2 flex items-center gap-2">
          <Check size={14} /> Saved at {savedAt.toLocaleTimeString()}
        </div>
      )}

      <WhatsappKillswitchCard />

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter templates…"
          className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          data-testid="whatsapp-settings-filter"
        />
        <span className="text-xs text-gray-500">{rows.length} template{rows.length === 1 ? '' : 's'}</span>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Message Type</th>
              {CHANNELS.map(c => (
                <th key={c.key} className="px-3 py-2 text-center" title={c.desc}>
                  {c.label}
                </th>
              ))}
              <th className="px-3 py-2 text-right">All</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={CHANNELS.length + 2} className="px-3 py-6 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={CHANNELS.length + 2} className="px-3 py-6 text-center text-gray-500">No templates match.</td></tr>
            )}
            {!loading && rows.map(([name, row]) => {
              const allOn = CHANNELS.every(c => row[c.key]);
              const allOff = CHANNELS.every(c => !row[c.key]);
              return (
                <tr
                  key={name}
                  data-testid={`whatsapp-row-${name}`}
                  className={`hover:bg-gray-50 ${allOff ? 'bg-red-50/40' : ''}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-gray-900">{row.label}</div>
                      {allOff && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700 border border-red-200"
                          data-testid={`whatsapp-silenced-chip-${name}`}
                          title="Every channel for this template is off — no messages of this type will be sent."
                        >
                          <VolumeX size={10} /> Silenced
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono">{name}</div>
                  </td>
                  {CHANNELS.map(c => (
                    <td key={c.key} className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!row[c.key]}
                        onChange={() => toggle(name, c.key)}
                        className="w-4 h-4 cursor-pointer accent-emerald-600"
                        data-testid={`whatsapp-toggle-${name}-${c.key}`}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setAllForRow(name, false)}
                        disabled={allOff}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-red-200 hover:bg-red-50 text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Silence — turn ALL channels off for this template"
                        data-testid={`whatsapp-row-silence-${name}`}
                      >
                        <VolumeX size={11} /> Silence
                      </button>
                      <button
                        onClick={() => setAllForRow(name, true)}
                        disabled={allOn}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-emerald-200 hover:bg-emerald-50 text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Enable — turn ALL channels on for this template"
                        data-testid={`whatsapp-row-enable-${name}`}
                      >
                        <Volume2 size={11} /> All on
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">
        Tip: <strong>Self</strong> means the staff who performed the action;
        <strong> Report 1/2/3</strong> map to the user's <code>reports_to_1/2/3</code> fields.
        Changes apply to *new* messages — already-sent notifications aren't affected.
      </p>

      <WhatsappTestSendCard />

      <WhatsappRecentMessagesCard />
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Kill-switch toggle card (June 18, 2026) — DB-backed override of the         //
// legacy WHATSAPP_KILL_SWITCH env var. Lets admin unmute production           //
// immediately, without a redeploy.                                             //
// --------------------------------------------------------------------------- //
function WhatsappKillswitchCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp/killswitch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setState(data);
    } catch (e) { setError(e.message || String(e)); }
  };

  useEffect(() => { load(); }, []);

  const setActive = async (next) => {
    if (busy) return;
    const confirmMsg = next
      ? 'Activate the kill switch? Every outgoing WhatsApp will be blocked until you turn it back off.'
      : 'Deactivate the kill switch? WhatsApp notifications will resume firing immediately.';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp/killswitch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active: next }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      await load();
    } catch (e) { setError(e.message || String(e)); }
    finally { setBusy(false); }
  };

  if (!state) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-xs text-gray-500" data-testid="whatsapp-killswitch-card">
        Loading kill switch state…
        {error && <div className="text-red-700 mt-1">{error}</div>}
      </div>
    );
  }

  const active = !!state.active;
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col md:flex-row md:items-center gap-3 ${active ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
      data-testid="whatsapp-killswitch-card"
    >
      <div className="flex items-center gap-2">
        {active ? <VolumeX size={20} className="text-red-700" /> : <Volume2 size={20} className="text-emerald-700" />}
        <div>
          <div className={`text-sm font-semibold ${active ? 'text-red-800' : 'text-emerald-800'}`} data-testid="whatsapp-killswitch-status">
            Kill switch is {active ? 'ACTIVE — all WhatsApp messages are blocked' : 'OFF — messages are delivering normally'}
          </div>
          <div className="text-[11px] text-gray-600">
            Source: <code>{state.source}</code>
            {state.source === 'env' && ' (legacy env var, no DB override yet)'}
            {state.updated_at && ` · last changed ${new Date(state.updated_at).toLocaleString()}`}
          </div>
        </div>
      </div>
      <div className="flex-1" />
      {active ? (
        <button
          onClick={() => setActive(false)}
          disabled={busy}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="whatsapp-killswitch-deactivate-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
          Deactivate (resume sending)
        </button>
      ) : (
        <button
          onClick={() => setActive(true)}
          disabled={busy}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="whatsapp-killswitch-activate-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <VolumeX size={14} />}
          Activate (block all sending)
        </button>
      )}
      {error && (
        <div className="md:basis-full text-xs text-red-700" data-testid="whatsapp-killswitch-error">{error}</div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Test-Send diagnostic card — fire a single message to verify the API key      //
// without simulating a real visit. Added June 18, 2026.                        //
// --------------------------------------------------------------------------- //
function WhatsappTestSendCard() {
  const [mobile, setMobile] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp/test-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mobile, message: message || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setResult(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="whatsapp-test-send-card">
      <div className="flex items-center gap-2">
        <Volume2 className="text-emerald-600" size={18} />
        <h2 className="font-semibold text-gray-900">Test send</h2>
        <span className="text-xs text-gray-500">Bypasses the silencing matrix to verify your WHATAPI key is live.</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          type="tel"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          placeholder="+91… recipient mobile"
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          data-testid="whatsapp-test-mobile"
        />
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optional message body"
          className="md:col-span-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          data-testid="whatsapp-test-message"
        />
        <button
          onClick={send}
          disabled={!mobile || busy}
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-lg text-sm flex items-center justify-center gap-1"
          data-testid="whatsapp-test-send-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
          {busy ? 'Sending…' : 'Send test'}
        </button>
      </div>
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="whatsapp-test-error">
          {error}
        </div>
      )}
      {result && (
        <div
          className={`text-xs rounded p-2 border ${result.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}
          data-testid="whatsapp-test-result"
        >
          <div><strong>Status:</strong> {result.status}</div>
          <div><strong>Recipient:</strong> {result.recipient || '—'}</div>
          <div><strong>API key configured:</strong> {result.has_api_key ? 'yes' : 'NO'}</div>
          <div><strong>Killswitch:</strong> {result.killswitch_active ? 'ACTIVE (blocking all sends)' : 'off'}</div>
          {result.api_response && (
            <div className="mt-1"><strong>API response:</strong> <code className="break-all">{result.api_response}</code></div>
          )}
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Recent WhatsApp Messages (June 18, 2026) — shows the actual message body    //
// each recipient saw, so the admin can identify a confusing template from a   //
// forwarded screenshot and one-click silence it.                              //
// --------------------------------------------------------------------------- //
function WhatsappRecentMessagesCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [silencingTpl, setSilencingTpl] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ limit: '50' });
      if (statusFilter) qs.set('status', statusFilter);
      const r = await fetch(`${API_URL}/api/admin/whatsapp/recent?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setRows(data.rows || []);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const visible = rows.filter((r) => {
    if (!filter) return true;
    const hay = `${r.template} ${r.recipient_name} ${r.recipient_mobile} ${r.message}`.toLowerCase();
    return hay.includes(filter.toLowerCase());
  });

  const silenceTemplate = async (tpl) => {
    if (!tpl) return;
    if (!window.confirm(`Silence the "${tpl}" template? Every channel will be unchecked — no future messages of this type will be sent until you re-enable a channel.`)) return;
    setSilencingTpl(tpl);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp/silence-template/${encodeURIComponent(tpl)}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      alert(`"${tpl}" silenced. Reload WhatsApp Settings to see the updated matrix.`);
    } catch (e) { alert(`Failed to silence: ${e.message}`); }
    finally { setSilencingTpl(''); }
  };

  const statusBadge = (s) => {
    const base = 'inline-block text-[10px] px-1.5 py-0.5 rounded font-medium';
    if (s === 'sent') return <span className={`${base} bg-emerald-100 text-emerald-800`}>sent</span>;
    if (s && s.startsWith('skipped')) return <span className={`${base} bg-amber-100 text-amber-800`}>{s}</span>;
    if (s && (s.startsWith('failed') || s === 'error')) return <span className={`${base} bg-red-100 text-red-800`}>{s}</span>;
    return <span className={`${base} bg-gray-100 text-gray-700`}>{s || '—'}</span>;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="whatsapp-recent-messages-card">
      <div className="flex items-center gap-2 flex-wrap">
        <MessageSquare className="text-indigo-600" size={18} />
        <h2 className="font-semibold text-gray-900">Recent messages</h2>
        <span className="text-xs text-gray-500">Preview the actual text recipients saw. One-click silence the template if it&apos;s noisy.</span>
        <div className="flex-1" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 border border-gray-200 rounded-lg text-xs"
          data-testid="recent-status-filter"
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="skipped_silenced">Skipped (silenced)</option>
          <option value="skipped_killswitch">Skipped (kill switch)</option>
          <option value="skipped_no_key">Skipped (no key)</option>
        </select>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search body / mobile / staff…"
          className="px-2 py-1 border border-gray-200 rounded-lg text-xs w-56"
          data-testid="recent-search-input"
        />
        <button
          onClick={load}
          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs flex items-center gap-1"
          data-testid="recent-refresh-btn"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
      {loading && <div className="text-xs text-gray-500">Loading…</div>}
      {!loading && visible.length === 0 && (
        <div className="text-xs text-gray-500 italic py-4 text-center">No messages match the current filter.</div>
      )}

      <div className="space-y-2 max-h-[460px] overflow-y-auto">
        {visible.map((r) => (
          <div
            key={r.id}
            className="border border-gray-200 rounded-lg p-2 hover:bg-gray-50"
            data-testid={`recent-row-${r.id}`}
          >
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-xs font-mono bg-indigo-50 text-indigo-800 px-1.5 py-0.5 rounded" data-testid={`recent-row-template-${r.id}`}>
                {r.template || '(no template)'}
              </span>
              {statusBadge(r.status)}
              <span className="text-[11px] text-gray-500">
                {new Date(r.sent_at).toLocaleString()}
              </span>
              <span className="text-[11px] text-gray-600">
                → <strong>{r.recipient_name}</strong> · {r.recipient_mobile}
              </span>
              <div className="flex-1" />
              {r.template && (
                <button
                  onClick={() => silenceTemplate(r.template)}
                  disabled={silencingTpl === r.template}
                  className="text-[11px] px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded flex items-center gap-1 disabled:opacity-50"
                  title="Un-check every channel for this template"
                  data-testid={`recent-silence-btn-${r.id}`}
                >
                  <BellOff size={11} />
                  {silencingTpl === r.template ? 'Silencing…' : 'Silence this template'}
                </button>
              )}
            </div>
            <pre
              className="mt-1 text-[11px] text-gray-800 bg-gray-50 border border-gray-100 rounded p-1.5 whitespace-pre-wrap break-words font-sans"
              data-testid={`recent-row-body-${r.id}`}
            >{r.message}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

